import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'
import type { Chapter, ThumbnailsConfig } from '../types'
import { captureFrameAt } from '../utils/captureFrame'
import { formatTime } from '../utils/formatTime'
import { loadVttThumbs, thumbAt, thumbFromVtt, type ThumbFrame } from '../utils/thumbnails'
import styles from './ProgressBar.module.css'

interface Props {
  currentTime: number
  duration: number
  buffered: number
  markers: number[]
  chapters?: Chapter[]
  thumbnails?: ThumbnailsConfig
  videoRef?: RefObject<HTMLVideoElement | null>
  disabled?: boolean
  onSeek: (time: number) => void
}

export function ProgressBar({
  currentTime,
  duration,
  buffered,
  markers,
  chapters = [],
  thumbnails,
  videoRef,
  disabled,
  onSeek,
}: Props) {
  const barRef = useRef<HTMLDivElement>(null)
  const scrubVideoRef = useRef<HTMLVideoElement | null>(null)
  const scrubReqRef = useRef(0)
  const lastHoverRef = useRef(0)
  const [srcTick, setSrcTick] = useState(0)

  const [hoverRatio, setHoverRatio] = useState<number | null>(null)
  const [dragging, setDragging] = useState(false)
  const [thumb, setThumb] = useState<ThumbFrame | null>(null)
  const [vttCues, setVttCues] = useState<
    { start: number; end: number; frame: ThumbFrame }[]
  >([])
  const [grabUrl, setGrabUrl] = useState<string | null>(null)

  useEffect(() => {
    const main = videoRef?.current
    if (!main) return
    const bump = () => setSrcTick((n) => n + 1)
    main.addEventListener('loadedmetadata', bump)
    main.addEventListener('loadeddata', bump)
    return () => {
      main.removeEventListener('loadedmetadata', bump)
      main.removeEventListener('loadeddata', bump)
    }
  }, [videoRef])

  useEffect(() => {
    const main = videoRef?.current
    if (!main) return
    const src = main.currentSrc || main.src
    if (!src) return

    let video = scrubVideoRef.current
    if (!video) {
      video = document.createElement('video')
      video.muted = true
      video.playsInline = true
      video.preload = 'auto'
      video.setAttribute('playsinline', '')
      if (main.crossOrigin) video.crossOrigin = main.crossOrigin
      video.style.cssText =
        'position:fixed;left:-9999px;top:0;width:160px;height:90px;opacity:0;pointer-events:none'
      document.body.appendChild(video)
      scrubVideoRef.current = video
    }

    if (video.getAttribute('src') !== src && video.src !== src) {
      video.src = src
      video.load()
    }
  }, [videoRef, srcTick])

  useEffect(() => {
    return () => {
      const video = scrubVideoRef.current
      if (!video) return
      video.removeAttribute('src')
      video.load()
      video.remove()
      scrubVideoRef.current = null
    }
  }, [])

  useEffect(() => {
    if (thumbnails && 'vttUrl' in thumbnails) {
      void loadVttThumbs(thumbnails.vttUrl).then(setVttCues)
    }
  }, [thumbnails])

  const ratioFromEvent = useCallback((clientX: number) => {
    const el = barRef.current
    if (!el) return 0
    const rect = el.getBoundingClientRect()
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
  }, [])

  const updatePreview = useCallback(
    (ratio: number) => {
      if (!duration) return
      const t = ratio * duration

      if (thumbnails && 'spriteUrl' in thumbnails) {
        setThumb(thumbAt(thumbnails, t))
        setGrabUrl(null)
        return
      }
      if (vttCues.length) {
        setThumb(thumbFromVtt(vttCues, t))
        setGrabUrl(null)
        return
      }

      setThumb(null)
      const scrub = scrubVideoRef.current
      if (!scrub) return

      const now = performance.now()
      if (now - lastHoverRef.current < 70) return
      lastHoverRef.current = now

      const req = ++scrubReqRef.current
      void captureFrameAt(scrub, t).then((url) => {
        if (req !== scrubReqRef.current) return
        if (url) setGrabUrl(url)
      })
    },
    [duration, thumbnails, vttCues],
  )

  const played = duration > 0 ? currentTime / duration : 0
  const buf = duration > 0 ? buffered / duration : 0
  const previewRatio = hoverRatio

  const onPointerDown = (e: React.PointerEvent) => {
    if (disabled || !duration) return
    e.currentTarget.setPointerCapture(e.pointerId)
    setDragging(true)
    const r = ratioFromEvent(e.clientX)
    setHoverRatio(r)
    onSeek(r * duration)
    updatePreview(r)
  }

  const onPointerMove = (e: React.PointerEvent) => {
    if (disabled) return
    const r = ratioFromEvent(e.clientX)
    setHoverRatio(r)
    updatePreview(r)
    if (dragging && duration) onSeek(r * duration)
  }

  const onPointerUp = (e: React.PointerEvent) => {
    if (dragging) {
      e.currentTarget.releasePointerCapture(e.pointerId)
      setDragging(false)
    }
  }

  return (
    <div
      ref={barRef}
      className={`${styles.bar} ${disabled ? styles.disabled : ''}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={() => {
        if (!dragging) {
          setHoverRatio(null)
          setThumb(null)
          setGrabUrl(null)
          scrubReqRef.current++
        }
      }}
      role="slider"
      aria-valuemin={0}
      aria-valuemax={duration || 0}
      aria-valuenow={currentTime}
      aria-label="Seek"
      tabIndex={disabled ? -1 : 0}
    >
      <div className={styles.track}>
        <div className={styles.buffered} style={{ width: `${buf * 100}%` }} />
        <div className={styles.played} style={{ width: `${played * 100}%` }} />
        {markers.map((m) =>
          duration > 0 ? (
            <span key={`ad-${m}`} className={styles.marker} style={{ left: `${(m / duration) * 100}%` }} />
          ) : null,
        )}
        {chapters.map((c) =>
          duration > 0 ? (
            <span
              key={`ch-${c.time}-${c.title}`}
              className={styles.chapter}
              style={{ left: `${(c.time / duration) * 100}%` }}
              title={c.title}
            />
          ) : null,
        )}
        <span className={styles.thumb} style={{ left: `${played * 100}%` }} />
      </div>
      {previewRatio != null && duration > 0 && (
        <div className={styles.tooltip} style={{ left: `${previewRatio * 100}%` }}>
          {(thumb || grabUrl) && (
            <div className={styles.thumbPreview}>
              {thumb ? (
                <div
                  className={styles.sprite}
                  style={{
                    width: thumb.w,
                    height: thumb.h,
                    backgroundImage: `url(${thumb.url})`,
                    backgroundPosition: `-${thumb.x}px -${thumb.y}px`,
                  }}
                />
              ) : (
                <img src={grabUrl!} alt="" width={160} height={90} />
              )}
            </div>
          )}
          <div className={styles.timeLabel}>
            {chapters.length > 0 && (
              <div className={styles.chapterLabel}>
                {[...chapters].reverse().find((c) => previewRatio * duration >= c.time)?.title || ''}
              </div>
            )}
            {formatTime(previewRatio * duration)}
          </div>
        </div>
      )}
    </div>
  )
}
